import { Capability } from './capabilities/capabilities.property.types';
import { Operators } from './operators.types';

/**
 * Represents the raw property data structure.
 * @internal
 */
export type PropertyRaw = {
	Property: string;
	Type: string;
	DisplayName: string;
	DeprecatedFieldName?: string;
	SubType?: string;
	SubSubType?: string;
	Precision?: number;
	Units?: string;
	UnitsDescription?: string;
	UnitsCode?: string;
	Category?: string;
	Properties?: PropertyRaw[];
	Required?: boolean;
	DefaultMax?: number;
	Calculated?: boolean;
	DbFieldNameOverride?: string;
	CachedFieldNameOverride?: string;
	Path?: string[];
} & {
	[capability in Capability]?: boolean;
};

/**
 * Represents a property, which can be single or nested.
 */
export type Property = SingleProperty | NestedProperty;

/**
 * Represents the base structure of a property.
 */
export interface PropertyBase {
	id: string;
	type: string;
	displayName: string;
	required: boolean;
	deprecatedFieldName?: string;
	category?: string;
	databaseField: string;
	cachedField: string;
	path: string[];
}

/**
 * Represents a single property.
 */
export type SingleProperty = GenericSingleProperty;

export const isSingleProperty = (
	object: Property,
): object is SingleProperty => {
	return object.type != 'List';
};

/**
 * Text-based property type enum
 */
export enum TextPropertyType {
	Text = 'Text',
	Memo = 'Memo',
}

/**
 * Numeric property type enum
 */
export enum NumericPropertyType {
	Double = 'Double',
	Integer = 'Integer',
	LongInteger = 'Long Integer',
}

/**
 * Boolean property type enum
 */
export enum BooleanPropertyType {
	Boolean = 'Boolean',
}

/**
 * Date/Time property type enum
 */
export enum DateTimePropertyType {
	DateTime = 'Date / Time',
}

/**
 * NOB (Number or Object) property type enum
 */
export enum NOBPropertyType {
	NOB = 'NOB',
}

/**
 * Represents the types of a generic single property.
 */
export type GenericSinglePropertyType =
	| TextPropertyType
	| NumericPropertyType
	| BooleanPropertyType
	| DateTimePropertyType
	| NOBPropertyType;

/**
 * Type guard to check if a value is a TextPropertyEnum
 * @param value - The value to check
 * @returns True if the value is a TextPropertyEnum value
 */
export function isTextProperty(value: string): value is TextPropertyType {
	return Object.values(TextPropertyType).includes(value as TextPropertyType);
}

/**
 * Type guard to check if a value is a NumericPropertyEnum
 * @param value - The value to check
 * @returns True if the value is a NumericPropertyEnum value
 */
export function isNumericProperty(value: string): value is NumericPropertyType {
	return Object.values(NumericPropertyType).includes(
		value as NumericPropertyType,
	);
}

/**
 * Type guard to check if a value is a BooleanPropertyEnum
 * @param value - The value to check
 * @returns True if the value is a BooleanPropertyEnum value
 */
export function isBooleanProperty(value: string): value is BooleanPropertyType {
	return Object.values(BooleanPropertyType).includes(
		value as BooleanPropertyType,
	);
}

/**
 * Type guard to check if a value is a DateTimePropertyEnum
 * @param value - The value to check
 * @returns True if the value is a DateTimePropertyEnum value
 */
export function isDateTimeProperty(
	value: string,
): value is DateTimePropertyType {
	return Object.values(DateTimePropertyType).includes(
		value as DateTimePropertyType,
	);
}

/**
 * Type guard to check if a value is a NOBPropertyEnum
 * @param value - The value to check
 * @returns True if the value is a NOBPropertyEnum value
 */
export function isNOBProperty(value: string): value is NOBPropertyType {
	return Object.values(NOBPropertyType).includes(value as NOBPropertyType);
}

/**
 * Type guard to check if a property represents a currency field
 * @param property - The property raw data or context with unitsCode
 * @returns True if the property is a currency field (has WN_COST unitsCode)
 */
export function isCurrencyProperty(context: { unitsCode?: string }): boolean {
	return context.unitsCode === 'WN_COST';
}

/**
 * Represents a generic single property.
 */
export interface GenericSingleProperty extends PropertyBase {
	type: GenericSinglePropertyType;
	subType?: string;
	subSubType?: string;
	precision?: number;
	units?: string;
	unitsDescription?: string;
	unitsCode?: string;
	dbFieldNameOverride?: string;
	cachedFieldNameOverride?: string;
	path: string[];

	/**
	 * Checks if the property has a specific capability.
	 * @param capability - The capability to check.
	 * @returns True if the property has the capability, false otherwise.
	 */
	hasCapability(capability: Capability): boolean;

	/**
	 * Gets the available operators for this property.
	 * This is useful for creating filters, queries, and UI components.
	 * @returns The available operators for this property.
	 */
	getOperators(): Operators;
}

/**
 * Represents a nested property.
 */
export interface NestedProperty extends PropertyBase {
	type: 'List';
	properties: SingleProperty[];

	/**
	 * Creates a copy of the nested property with new properties.
	 * @internal
	 * @param properties - The new properties.
	 * @returns A new nested property with the updated properties.
	 */
	copyWithNewProperties(properties: SingleProperty[]): NestedProperty;
}
